<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch, withDefaults } from "vue";

const props = withDefaults(
  defineProps<{
    filter: any;
    activeValue: string[];
  }>(),
  {
    filter: () => ({}),
    activeValue: () => [],
  }
);

const emit = defineEmits<{
  (e: "update", value: string[]): void;
}>();

const valuesKeys = computed(() => Object.keys(props.filter.values || {}));
const valuesCount = computed(() => valuesKeys.value.length);

const sliderRef = ref<HTMLElement | null>(null);
const isDragging = ref<'min' | 'max' | null>(null);

// Get initial indices from activeValue
const getInitialIndices = () => {
  if (!props.activeValue || props.activeValue.length === 0) {
    return { min: 0, max: valuesCount.value - 1 };
  }
  const indices = props.activeValue.map(v => valuesKeys.value.indexOf(v)).filter(i => i !== -1);
  if (indices.length === 0) return { min: 0, max: valuesCount.value - 1 };
  return {
    min: Math.min(...indices),
    max: Math.max(...indices)
  };
};

const initialIndices = getInitialIndices();
const localMinIdx = ref(initialIndices.min);
const localMaxIdx = ref(initialIndices.max);

watch(() => props.activeValue, () => {
  if (!isDragging.value) {
    const indices = getInitialIndices();
    localMinIdx.value = indices.min;
    localMaxIdx.value = indices.max;
  }
}, { deep: true });

const getPercentage = (index: number) => {
  if (valuesCount.value <= 1) return 0;
  return (index / (valuesCount.value - 1)) * 100;
};

const minPercent = computed(() => getPercentage(localMinIdx.value));
const maxPercent = computed(() => getPercentage(localMaxIdx.value));

const handleMouseDown = (type: 'min' | 'max') => {
  isDragging.value = type;
};

const handleMouseMove = (e: MouseEvent | TouchEvent) => {
  if (!isDragging.value || !sliderRef.value) return;

  const rect = sliderRef.value.getBoundingClientRect();
  const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
  const offsetX = clientX - rect.left;
  const percentage = Math.max(0, Math.min(100, (offsetX / rect.width) * 100));
  
  const index = Math.round((percentage / 100) * (valuesCount.value - 1));

  if (isDragging.value === 'min') {
    localMinIdx.value = Math.min(index, localMaxIdx.value);
  } else {
    localMaxIdx.value = Math.max(index, localMinIdx.value);
  }
};

const handleMouseUp = () => {
  if (isDragging.value) {
    const selectedValues = valuesKeys.value.slice(localMinIdx.value, localMaxIdx.value + 1);
    emit("update", selectedValues);
  }
  isDragging.value = null;
};

const handleLabelClick = (index: number) => {
  const distMin = Math.abs(index - localMinIdx.value);
  const distMax = Math.abs(index - localMaxIdx.value);

  if (distMin <= distMax) {
    localMinIdx.value = index;
  } else {
    localMaxIdx.value = index;
  }

  const selectedValues = valuesKeys.value.slice(localMinIdx.value, localMaxIdx.value + 1);
  emit("update", selectedValues);
};

onMounted(() => {
  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseup', handleMouseUp);
  window.addEventListener('touchmove', handleMouseMove, { passive: false });
  window.addEventListener('touchend', handleMouseUp);
});

onUnmounted(() => {
  window.removeEventListener('mousemove', handleMouseMove);
  window.removeEventListener('mouseup', handleMouseUp);
  window.removeEventListener('touchmove', handleMouseMove);
  window.removeEventListener('touchend', handleMouseUp);
});

const getImageUrl = (value: string) => {
  if (!props.filter.image) return '';
  const processedVal = props.filter.lowercaseImage !== false ? value.toLowerCase() : value;
  return props.filter.image.replace("{value}", processedVal);
};

</script>

<template>
  <div class="diamonds-search-form-range pt-4 pb-2 px-10">
    <!-- Icon display above slider -->
    <div v-if="filter.type === 'icon'" class="relative !w-full h-16 mb-4">
      <div 
        v-for="(key, index) in valuesKeys"
        :key="'icon-' + key"
        class="absolute -translate-x-1/2 bottom-0 transition-all duration-100 cursor-pointer w-12"
        :style="{ left: getPercentage(index) + '%' }"
        @click="handleLabelClick(index)"
      >
        <img 
          :src="getImageUrl(key)" 
          class="w-12 h-12 object-contain rounded-full bg-white transition-opacity"
          :class="index >= localMinIdx && index <= localMaxIdx ? 'opacity-100' : 'opacity-40'"
          alt="" 
        />
      </div>
    </div>

    <!-- Slider -->
    <div class="relative w-full h-1 bg-gray-200 rounded-full mb-10" ref="sliderRef">
      <!-- Active range bar -->
      <div 
        class="absolute h-full bg-brand-main rounded-full"
        :style="{ left: minPercent + '%', right: (100 - maxPercent) + '%' }"
      ></div>

      <!-- Min handle -->
      <div 
        class="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 bg-brand-main border-2 border-brand-main rounded-full cursor-pointer z-10 touch-none shadow-sm"
        :style="{ left: minPercent + '%' }"
        @mousedown="handleMouseDown('min')"
        @touchstart.prevent="handleMouseDown('min')"
      >
      </div>

      <!-- Max handle -->
      <div 
        class="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 bg-brand-main border-2 border-brand-main rounded-full cursor-pointer z-10 touch-none shadow-sm"
        :style="{ left: maxPercent + '%' }"
        @mousedown="handleMouseDown('max')"
        @touchstart.prevent="handleMouseDown('max')"
      >
      </div>

      <!-- Ticks / Labels -->
      <div class="absolute top-6 left-0 w-full px-[2px]">
        <div 
          v-for="(key, index) in valuesKeys" 
          :key="key"
          class="flex flex-col items-center absolute cursor-pointer group"
          style="width: 0; overflow: visible;"
          :style="{ left: getPercentage(index) + '%' }"
          @click="handleLabelClick(index)"
        >
          <div class="w-[1px] h-1.5 bg-gray-300 -translate-y-6 group-hover:bg-brand-main"></div>
          <span class="text-[10px] text-gray-500 whitespace-nowrap mt-1 uppercase tracking-tighter font-medium text-center group-hover:text-brand-main">
            {{ filter.values[key] }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.diamonds-search-form-range {
  user-select: none;
}
</style>